import React from 'react';
import InfoIcon from '../InfoIcon';
import ComplianceBar from './ComplianceBar';
import ComplianceBadge, { badgeStateFor } from './ComplianceBadge';
import { approvedCurrentCount } from '../../lib/compliance';

// At-a-glance card on the top of /portal/compliance. Shows:
//   - the X/Y approved fraction (large numbers, X coloured by state)
//   - "Required for new work" + the outstanding count
//   - a progress bar SEGMENTED by state (approved / awaiting / expiring /
//     expired), so it reflects documents in progress – not just approved.
//     Outstanding is the un-filled grey remainder.
//   - five pill counters (Approved / Awaiting Review / Expiring Soon / Outstanding / Expired)
//
// Pure presentational – takes counts + state, renders. No data fetching.

// The segmented progress bar lives in ComplianceBar (shared with the dashboard
// card). Build #18 §2a: green FIGURE is deep-green #009159 (acid #92D400 fails
// contrast as text on white; deep-green is the token for green numbers/
// labels – matches the "grand-total in deep green" already elsewhere).
const STATE_FRACTION_HEX = {
  green: '#009159',
  amber: '#E89A2E',
  red:   '#E0290A',
};

// Each state carries a SHAPE as well as a colour (tidy-up §5). Outstanding and
// Expired are both red now — deliberately, since expired is at least as serious
// — so hue alone no longer separates them, and it never separated them at all
// for anyone who can't see red. The glyph does the work colour was doing on its
// own.
const PILLS = [
  { key: 'approved',       label: 'Approved',         dot: '#92D400', glyph: '✓' },
  { key: 'awaitingReview', label: 'Awaiting Review',  dot: '#2E8FA1', glyph: '⋯' },
  { key: 'expiringSoon',   label: 'Expiring Soon',    dot: '#E89A2E', glyph: '!' },
  { key: 'outstanding',    label: 'Outstanding',      dot: '#E0290A', glyph: '•' },
  { key: 'expired',        label: 'Expired',          dot: '#8E1B06', glyph: '✕' },
];

export default function ComplianceStateCard({ state, counts }) {
  const { outstanding = 0, expired = 0, needsClarification = 0, awaitingReview = 0, total = 0 } = counts || {};
  // The fraction counts expiring-soon docs as approved (they are – just with a
  // renewal deadline), so "all four approved, one expiring" reads 4/4 in amber
  // rather than 3/4. The pills below still break the states out separately.
  const approved = approvedCurrentCount(counts);
  const fractionColour = STATE_FRACTION_HEX[state] || STATE_FRACTION_HEX.green;
  // "Required for new work" is blocked by any doc that isn't current: rejected
  // (→ counted as outstanding), expired, or needs-clarification (compliance
  // review-loop brief §1). Awaiting-review is NOT blocking – the supplier has
  // done their part.
  const outstandingBlocking = outstanding + expired + needsClarification;

  return (
    <div className="bg-white border border-[#E6EADB] rounded-2xl shadow-lift p-6 sm:p-7 mb-8">
      <div className="flex items-start justify-between mb-5 gap-4">
        <div className="flex items-center gap-4">
          <ComplianceBadge state={badgeStateFor(state, counts)} size="md" counts={counts} />
          <div>
            <div className="font-['Metropolis'] font-extrabold text-[32px] tabular-nums leading-none">
              <span style={{ color: fractionColour }}>{approved}</span>
              <span className="text-[22px]" style={{ color: '#7C7F6F' }}>/{total}</span>
            </div>
            <div className="font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.18em] text-fusion-mid-grey mt-2">
              {/* §4 — ONE tooltip in this card, not three. Two were explaining
                  LABELS, which the brief is right to treat as a sign the labels
                  are wrong. This one said "how many of your required documents
                  are approved, and all of them are needed before we can award
                  you work" — the fraction above says the first half and the
                  block to the right says the second. Pure duplication. */}
              Approved
            </div>
          </div>
        </div>
        <div className="text-right">
          <div className="font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.18em] text-fusion-mid-grey mb-1.5">
            {/* Needed a tooltip because the label named a CATEGORY rather than
                saying what the number means. This says it outright, and the
                value below already reads "All current" or "N outstanding". */}
            Blocking new work
          </div>
          <div
            className="font-['Metropolis'] text-[11px] font-extrabold uppercase tracking-[0.15em]"
            style={{ color: outstandingBlocking > 0 ? '#E0290A' : '#009159' }}
          >
            {outstandingBlocking === 0
              ? 'All current'
              : `${outstandingBlocking} outstanding`}
          </div>
          {/* Awaiting-review isn't blocking (the supplier has done their part),
              but it isn't approved yet either, so surface it as a secondary
              line under the outstanding count for visibility. */}
          {awaitingReview > 0 && (
            <div
              className="font-['Metropolis'] text-[10px] font-bold uppercase tracking-[0.15em] mt-1"
              style={{ color: '#2E8FA1' }}
            >
              {awaitingReview} awaiting review
            </div>
          )}
        </div>
      </div>

      <ComplianceBar counts={counts} className="mb-5" />

      <div className="flex items-center gap-1.5">
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-x-4 gap-y-3 flex-1 min-w-0">
          {PILLS.map(pill => (
            <div key={pill.key} className="flex items-center gap-2 font-['Metropolis'] text-[11px] text-fusion-mid-grey font-semibold">
              <span aria-hidden="true" className="w-3.5 h-3.5 rounded-full flex-shrink-0 flex items-center justify-center text-[8px] font-bold leading-none text-white" style={{ backgroundColor: pill.dot }}>{pill.glyph}</span>
              <span>
                <span className="font-extrabold text-ink tabular-nums">{counts?.[pill.key] || 0}</span>
                {' '}{pill.label}
              </span>
            </div>
          ))}
        </div>
        <InfoIcon heading="What each status means" width={InfoIcon.WIDE} ariaLabel="What does each status mean?">
          <span className="block mb-1.5"><strong style={{ color: '#009159' }}>Approved</strong> – Fusion 2K has reviewed and accepted this document. It counts towards your compliance.</span>
          <span className="block mb-1.5"><strong style={{ color: '#2E8FA1' }}>Awaiting Review</strong> – you've uploaded it and Fusion 2K is checking it.</span>
          <span className="block mb-1.5"><strong style={{ color: '#E89A2E' }}>Expiring Soon</strong> – approved, but within 30 days of its expiry date. Upload a renewal before it lapses.</span>
          <span className="block mb-1.5"><strong style={{ color: '#E0290A' }}>Outstanding</strong> – required and not yet provided (never uploaded, or returned for a fix).</span>
          <span className="block"><strong style={{ color: '#8E1B06' }}>Expired</strong> – past its expiry date and no longer valid. Upload a current version.</span>
        </InfoIcon>
      </div>
    </div>
  );
}
